import * as React from "react";
import { Search } from "lucide-react";
import { cn } from "../utils/cn";
import { Input } from "./input";

/**
 * A text input with a search affordance.
 *
 * Extracted because ten pages had already hand-rolled the same three lines — a relative wrapper, an
 * absolutely-positioned `Search` icon, and an `Input` with `pl-9` — and each copy had drifted a
 * little in its icon size, offset and placeholder. This is that markup once.
 *
 * `label` is the accessible name and is required. Every copy of the old markup relied on the
 * placeholder for that, which a screen reader may not announce and which vanishes the moment
 * somebody types.
 */
export interface SearchInputProps extends Omit<React.ComponentProps<typeof Input>, "type" | "onChange" | "value"> {
    value: string;
    onValueChange: (value: string) => void;
    /** The accessible name. Falls back to being the placeholder too when none is given. */
    label: string;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
    ({ className, value, onValueChange, label, placeholder, ...props }, ref) => (
        <div className="relative min-w-48 flex-1">
            <Search
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                aria-hidden="true"
            />
            <Input
                ref={ref}
                type="search"
                aria-label={label}
                placeholder={placeholder ?? label}
                className={cn("pl-9", className)}
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                {...props}
            />
        </div>
    ),
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
